import type { AccountabilityRecord, Official, OfficialActivity } from '../../apiClient';

export type ReceiptMomentKind = 'accountability_record' | 'activity' | 'dossier_refresh';

export interface ReceiptMoment {
  id: string;
  anchorId: string;
  kind: ReceiptMomentKind;
  claim: string;
  summary: string;
  occurredAt: number;
  impactLabel: string;
  evidenceLabel: string;
  evidenceUrl: string | null;
}

function normalizeText(value: string | null | undefined, fallback: string): string {
  const normalized = String(value ?? '').replace(/\s+/g, ' ').trim();
  return normalized.length > 0 ? normalized : fallback;
}

function humanizeToken(value: string | null | undefined, fallback: string): string {
  const normalized = String(value ?? '').trim();
  if (!normalized) return fallback;
  return normalized.replace(/_/g, ' ');
}

function truncate(value: string, max = 180): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1).trimEnd()}…`;
}

function asTimestamp(...values: Array<number | null | undefined>): number | null {
  for (const value of values) {
    if (Number.isFinite(value)) return Number(value);
  }
  return null;
}

function sortNewestFirst(a: ReceiptMoment, b: ReceiptMoment): number {
  return b.occurredAt - a.occurredAt;
}

export function buildReceiptMoments(input: {
  official: Pick<Official, 'id' | 'name' | 'office_name' | 'updated_at' | 'accountability_summary'>;
  accountabilityRecords?: AccountabilityRecord[];
  activity?: OfficialActivity[];
}): ReceiptMoment[] {
  const officialName = input.official.name;
  const accountabilityMoments = (input.accountabilityRecords ?? [])
    .map((record): ReceiptMoment | null => {
      const occurredAt = asTimestamp(record.record_date, record.created_at);
      if (!occurredAt) return null;
      return {
        id: record.id,
        anchorId: `receipt-record-${record.id}`,
        kind: 'accountability_record',
        claim: normalizeText(record.title, `New accountability record for ${officialName}`),
        summary: truncate(normalizeText(record.description, humanizeToken(record.record_type, 'Accountability record added to this dossier.'))),
        occurredAt,
        impactLabel: humanizeToken(record.impact_type, 'under review'),
        evidenceLabel: record.evidence_url ? 'Primary evidence' : 'Evidence pending',
        evidenceUrl: record.evidence_url,
      };
    })
    .filter((moment): moment is ReceiptMoment => Boolean(moment));

  const activityMoments = (input.activity ?? [])
    .map((item): ReceiptMoment | null => {
      const occurredAt = asTimestamp(item.activity_date);
      if (!occurredAt) return null;
      return {
        id: item.id,
        anchorId: `receipt-activity-${item.id}`,
        kind: 'activity',
        claim: normalizeText(item.title, `${officialName} posted new public activity`),
        summary: truncate(normalizeText(item.description, humanizeToken(item.activity_type, 'Recent public activity was recorded.'))),
        occurredAt,
        impactLabel: humanizeToken(item.activity_type, 'activity'),
        evidenceLabel: item.source_url ? 'Activity source' : 'Source pending',
        evidenceUrl: item.source_url,
      };
    })
    .filter((moment): moment is ReceiptMoment => Boolean(moment));

  const moments = [...accountabilityMoments, ...activityMoments].sort(sortNewestFirst);
  if (moments.length > 0) return moments;

  return [{
    id: 'dossier-refresh',
    anchorId: 'receipt-dossier-refresh',
    kind: 'dossier_refresh',
    claim: `${officialName}'s dossier was refreshed`,
    summary: truncate(normalizeText(input.official.accountability_summary, 'The dossier has been refreshed, but a source-backed movement record has not been attached yet.')),
    occurredAt: input.official.updated_at,
    impactLabel: 'dossier refresh',
    evidenceLabel: 'Dossier page',
    evidenceUrl: null,
  }];
}

export function buildReceiptSharePath(options: {
  officialId: string;
  anchorId: string;
  source?: string;
}): string {
  const params = new URLSearchParams({
    utm_source: options.source ?? 'clipboard',
    utm_medium: 'receipt',
    utm_campaign: 'accountability_share',
    receipt: options.anchorId,
  });
  return `/officials/${encodeURIComponent(options.officialId)}?${params.toString()}#${encodeURIComponent(options.anchorId)}`;
}

export function buildReceiptShareUrl(options: {
  origin?: string;
  officialId: string;
  anchorId: string;
  source?: string;
}): string {
  const path = buildReceiptSharePath(options);
  if (options.origin) return `${options.origin}${path}`;
  if (typeof window !== 'undefined' && window.location?.origin) return `${window.location.origin}${path}`;
  return path;
}

export function buildReceiptShareText(options: {
  officialName: string;
  officeName: string;
  moment: ReceiptMoment;
}): string {
  const dateLabel = formatReceiptTimestamp(options.moment.occurredAt);
  return `${options.officialName} — ${options.officeName}\n${options.moment.claim}\n${options.moment.summary}\nEvidence timestamp: ${dateLabel}`;
}

export function formatReceiptTimestamp(timestamp: number | null | undefined): string {
  if (!Number.isFinite(timestamp)) return 'Timestamp unavailable';
  return new Date(Number(timestamp)).toLocaleString();
}
